import { ListKeyManager } from "@angular/cdk/a11y";
import { DOWN_ARROW, ENTER, UP_ARROW } from "@angular/cdk/keycodes";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostListener,
	inject,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	QueryList,
	SimpleChanges,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from "@angular/forms";
import * as Sentry from "@sentry/angular";
import { BehaviorSubject, EMPTY, Subject } from "rxjs";
import { catchError, takeUntil, tap } from "rxjs/operators";
import { v4 as uuidV4 } from "uuid";

import { SearchItem, SearchKeyOptions } from "./interfaces";
import { fuzzy } from "./search.util";

const SELECT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => SearchSelectComponent),
	multi: true,
};

@Component({
	selector: "ic-search",
	templateUrl: "./search.component.html",
	providers: [SELECT_CONTROL_ACCESSOR],
	styleUrls: ["./search.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class SearchSelectComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
	private cdRef = inject(ChangeDetectorRef);
	private eRef = inject(ElementRef);
	private fb = inject(UntypedFormBuilder);
	private ngZone = inject(NgZone);

	//  The following inputs are standards used within the into.care input module.
	@Input() icon: string;
	@Input() actionIcon = "fa fa-search";
	@Input() placeholder: string;
	@Input() label: string;
	@Input() help: string;
	@Input() required = true;
	@Input() missing = false;
	@Input() missingText: string;
	// Ties the label to the input and lets aria-controls point at the listbox. Overridable, following ic-toggle.
	@Input() id = uuidV4();

	// Optional key of the item we want to have as our formValue (i.e 'name' of {id: 1, name 'Hello'}).
	@Input() key: string;
	// Optional key of the item we want to match with in the writeValue.
	@Input() valueKey: string;
	// Optional searchKey string or strings of the item we want to match with (i.e ['fullname','name'] for [{id: 1, name: 'Hello'} {id: 1, fullname: 'Hello'}]).
	@Input() searchKey: string | string[];
	// Optional searchKeyOptions to determine how we should match the searchKeys (either 'matchAll' or 'fallback')
	@Input() searchKeyOptions: SearchKeyOptions = { searchKeyType: "matchAll" };
	// Minimum amount of characters needed to start searching.
	@Input() minLength = 2;
	// Allow multiple items to be selected as a formValue.
	@Input() multi = false;
	// Items used in the searchList.
	@Input() items: any[] = [];
	@Input() stayOpen = false;
	@Input() updatingItems: boolean;
	// Boolean indicating if control is disabled.
	@Input() disabled = false;
	// Readonly value to overrule selected value when disabled without options.
	@Input() disabledValue: string;

	/**
	 * By default we will use the `key` property of the item to set the value of the data-test attribute. With this
	 * attribute you can use another property of the item to set the data-test attribute.
	 */
	@Input() customDataTestAttribute: string;

	// Output used in the action template.
	@Output() action = new EventEmitter<void>();
	@Output() valueChange = new EventEmitter<string>();

	// Output on select
	@Output() selectItem = new EventEmitter<any>();

	// Optional template to style the items in the list.
	@ContentChild("item", { static: false }) itemTemplate: TemplateRef<any>;
	// Optional template to style the selected items in the input itself when selected.
	@ContentChild("placeholder", { static: false }) placeholderTemplate: TemplateRef<any>;
	// Optional template to style the selected items in the list.
	@ContentChild("selected", { static: false }) selectedTemplate: TemplateRef<any>;
	// Template to style the list when there are no items.
	@ContentChild("empty", { static: false }) emptyTemplate: TemplateRef<any>;
	// Optional template to add an extra action add the bottom of the list.
	@ContentChild("action", { static: false }) actionTemplate: TemplateRef<any>;
	@ContentChild("loading", { static: false }) loadingTemplate: TemplateRef<any>;

	// Search input field.
	@ViewChild("input", { static: true }) input: ElementRef;
	// UL-element containing the filteredItems, selectedItems and the action.
	@ViewChild("list", { static: false }) list: ElementRef;
	// Search menu
	@ViewChild("searchMenu", { static: false }) searchMenu: ElementRef;
	// Manger for keyboard events.
	@ViewChildren("listItem") listItems: QueryList<any>;

	// Set containing all the selected items
	selected = new Set<any>();
	// Selected value or values needed for the formValue, influenced by the key property.
	value: any | any[];
	// Form used for the input search field.
	innerForm = this.fb.group({
		value: [""],
	});

	// Actual selected value, independent of the key property.
	selectedValue: any | any[];
	// Fallback for when placeholderTemplate is not included.
	fallbackPlaceholder: string;

	// Array of fuzzy filtered items.
	filteredItems: any[] = [];
	// Array of fuzzy filtered selected items.
	selectedItems: any[] = [];

	// Manger for keyboard events.
	keyboardEventsManager: ListKeyManager<any>;
	// Current index for arrow-key navigation.
	itemIndex = 0;

	// Subject to track onDestroy event.
	private destroy$ = new Subject<void>();

	// Listener to handle outside click.
	private listener = this.onClick.bind(this);

	// Indicates if there are predefined options.
	private hasOptions: boolean;
	// Set of selected items, required to re-use the item-template in case the selected-template is not passed. */
	private selectedSet = new Set();
	// Array of items we'll filter.
	private toFilterItems: any[] = [];
	protected loading$ = new BehaviorSubject<boolean>(false);
	protected showList$ = new BehaviorSubject<boolean>(false);

	// Bind control functions.
	private onTouch = () => {};
	private onChange = (_: unknown) => {};

	// Prevent default
	@HostListener("keyUp")
	keyUp() {
		return false;
	}

	/**
	 * Select or deselect item from the list.
	 *
	 * @param item - An item.
	 */
	selectOption(item: any) {
		if (!item) {
			return;
		}

		const handleSelect = () => {
			this.ngZone.run(() => {
				this.onTouch();
				const selected = this.selectionKey(item);

				if (this.selected.has(selected)) {
					this.selected.delete(selected);
					this.selectedSet.delete(item);
				} else {
					if (!this.multi) {
						this.selected = new Set([]);
						this.selectedSet = new Set([]);
					}

					this.selected.add(selected);
					this.selectedSet.add(item);
				}

				const result = Array.from(this.selected);

				this.value = (this.multi ? result : result[0]) || null;

				// This will ensure no empty value can be sent to the formControl
				if (this.multi && this.value.length === 0) {
					this.value = null;
				}

				this.onChange(this.value);
				this.selectItem.emit(this.value);

				// Creates value to display the placeholder when needed
				if (this.placeholderTemplate) {
					this.selectedValue = this.multi ? Array.from(this.selectedSet) : item;
				} else {
					this.fallbackPlaceholder = this.multi ? result.join(", ") : result[0];
				}

				this.toFilterItems = this.availableItems();
				this.filteredItems = this.toFilterItems;

				// Reset selectedItems to before we filtered
				this.selectedItems = Array.from(this.selectedSet);

				// Reset the search input value
				this.clearSearchForm();
				this.cdRef.markForCheck();
			});
		};

		if (this.stayOpen) {
			setTimeout(() => {
				handleSelect();
			});

			return;
		}

		handleSelect();
	}

	private clearSearchForm() {
		if (this.innerForm.value) {
			this.innerForm.reset();
		}
	}

	onFocus() {
		if (this.hasOptions && !this.showList$.getValue()) {
			this.showList$.next(true);
		}
	}

	// @Override
	registerOnChange(fn: (v: unknown) => void) {
		this.onChange = fn;
	}

	// @Override
	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	// @Override
	/**
	 * Set value when formValue already exists on init.
	 *
	 * @param value - An item or an array of items.
	 */
	writeValue(value: any | any[]) {
		this.clearSearchForm();
		this.value = value;

		// If we have a valueKey, the value we patch to the form is of a different type than we expect to emit onChange. We must convert it first.
		if (this.valueKey && value) {
			if (this.multi) {
				this.value = [];

				for (const val of value) {
					this.value.push(val[this.valueKey]);
				}
			} else {
				this.value = value[this.valueKey];
			}
		}

		this.selected = new Set(this.multi ? this.value : [this.value]);

		// Setup the selected item list for the visualization
		if (value) {
			this.selectedSet = this.key ? new Set(this.chosenItems()) : new Set(this.multi ? this.value : [this.value]);
		} else {
			this.selectedSet = new Set([]);
		}

		if (this.multi) {
			this.selectedValue = Array.from(this.selectedSet);
		} else {
			this.selectedValue = this.key && this.items ? this.items.find((i) => i[this.key] === this.value) : this.value;
		}

		this.toFilterItems = this.availableItems();
		this.filteredItems = [...this.toFilterItems];
		this.selectedItems = Array.from(this.selectedSet);

		this.cdRef.markForCheck();
	}

	/**
	 * Disable control.
	 *
	 * @param isDisabled - Disable state.
	 */
	setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;

		if (isDisabled) {
			this.innerForm.disable();
		} else {
			this.innerForm.enable();
		}
	}

	// Emit the action when clicked.
	onAction() {
		this.action.emit();
	}

	/**
	 * Shared by the `option-` and `chosen-` data-test prefixes, so an item keeps the same handle whether it is still
	 * available or already chosen. The QA page object relies on that.
	 *
	 * @param item - An item of the list.
	 */
	itemDataTest(item: SearchItem) {
		const attribute = this.customDataTestAttribute || this.key;

		return String(attribute ? item[attribute] : item.id);
	}

	/**
	 * Handle keyUp event in order to work with keyboard navigation.
	 *
	 * @param event - Keyboard event.
	 */
	handleKeyUp(event: KeyboardEvent) {
		if (event.keyCode === DOWN_ARROW || event.keyCode === UP_ARROW) {
			this.navigate(event);
		} else if (event.keyCode === ENTER) {
			this.selectOption(this.findItemFromElement());
		} else {
			return;
		}

		event.stopPropagation();
		return false;
	}

	// Attach mouse-event listener.
	private listenToClick() {
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("click", this.listener);
		});
	}

	// Remove mouse-event listener.
	private removeClick() {
		window.removeEventListener("click", this.listener);
	}

	/**
	 * Handle outside clicks.
	 *
	 * @param event - Mouse event.
	 */
	private onClick(event: MouseEvent) {
		event.preventDefault();
		const hostContainsElementClicked = this.eRef.nativeElement.contains(event.target);
		const searchDropdownContainsElementClicked =
			this.searchMenu && this.searchMenu.nativeElement.contains(event.target);

		if (!this.stayOpen && hostContainsElementClicked && !searchDropdownContainsElementClicked) {
			return;
		}

		if (this.stayOpen && (hostContainsElementClicked || searchDropdownContainsElementClicked)) {
			return;
		}

		this.ngZone.run(() => {
			if (this.showList$.getValue()) {
				this.showList$.next(false);
				this.cdRef.markForCheck();
			}
		});
	}

	/**
	 * Navigate up or down the list of items.
	 *
	 * @param event - Keyboard event.
	 */
	private navigate(event: KeyboardEvent) {
		const items = this.listItems.toArray();
		const isDown = event.keyCode === DOWN_ARROW;

		const current = items[this.itemIndex];
		const previous = isDown ? items[this.itemIndex - 1] : items[this.itemIndex + 1];

		if (current) {
			current.nativeElement.focus();
			this.list.nativeElement.scrollTo(0, current.nativeElement.clientHeight * this.itemIndex);
		}

		if (previous) {
			previous.nativeElement.blur();
		}

		const newIndex = isDown ? this.itemIndex + 1 : this.itemIndex - 1;
		const filtered = this.selectedItems.length + this.filteredItems.length;

		if (newIndex >= 0 && newIndex <= filtered) {
			this.itemIndex = newIndex;
		}
	}

	// Find the currently focussed on item in the list.
	private findItemFromElement() {
		const index = this.itemIndex === 0 ? this.itemIndex : this.itemIndex - 1;
		if (this.multi && index < this.selectedItems.length) {
			return this.selectedItems[index];
		}

		return this.filteredItems[index];
	}

	ngOnInit() {
		this.filteredItems = this.items;
		this.toFilterItems = this.items;
		this.showList$.pipe(takeUntil(this.destroy$)).subscribe((visible) => {
			if (visible) {
				this.listenToClick();
				this.input.nativeElement.focus();

				return;
			}

			this.removeClick();
			this.clearSearchForm();
		});

		this.innerForm
			.get("value")
			.valueChanges.pipe(
				tap((value) => {
					this.filteredItems = this.filterItems(this.toFilterItems, value);
					this.selectedItems = this.filterItems(Array.from(this.selectedSet), value);
					this.valueChange.emit(value);
				}),
				catchError((err: unknown) => {
					Sentry.captureException(
						new Error("Unable to correctly filter items inside search component", { cause: err }),
					);
					return EMPTY;
				}),
				takeUntil(this.destroy$),
			)
			.subscribe();

		this.keyboardEventsManager = new ListKeyManager(this.listItems);
	}

	private filterItems<T>(items: T[], value: string | undefined): T[] {
		if (value && this.searchKey?.length > 0) {
			return fuzzy(items, value, this.searchKey, this.searchKeyOptions);
		} else {
			return items;
		}
	}

	private selectionKey(item: SearchItem): unknown {
		return this.key ? item[this.key] : item;
	}

	private availableItems(): SearchItem[] {
		return (this.items ?? []).filter((item) => !this.selected.has(this.selectionKey(item)));
	}

	private chosenItems(): SearchItem[] {
		return (this.items ?? []).filter((item) => this.selected.has(this.selectionKey(item)));
	}

	/**
	 * A form value is often written before the items arrive (`[items]="something$ | async"`), which leaves writeValue
	 * building the chosen side from an empty list: `selected` is populated but `selectedSet` is not, so the item is
	 * filtered out of the available options without ever appearing as a chosen one, invisible in the open dropdown.
	 *
	 * @param filterValue - Current content of the search field, so a chosen item stays hidden while it is filtered out.
	 */
	private restoreChosenItems(filterValue: string | undefined) {
		if (this.selected.size === 0 || this.selectedSet.size > 0) {
			return;
		}

		this.selectedSet = new Set(this.chosenItems());

		if (this.multi) {
			this.selectedValue = Array.from(this.selectedSet);
		} else if (this.key && this.items) {
			this.selectedValue = this.items.find((item) => item[this.key] === this.value) ?? this.selectedValue;
		}

		this.selectedItems = this.filterItems(Array.from(this.selectedSet), filterValue);
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!changes.items) {
			return;
		}

		if (!this.hasOptions) {
			this.hasOptions = true;
		}

		const filterValue = this.innerForm.get("value").value;

		this.restoreChosenItems(filterValue);

		this.toFilterItems = this.availableItems();
		this.filteredItems = this.filterItems(this.toFilterItems, filterValue);

		this.loading$.next(
			this.updatingItems === undefined
				&& changes.items.previousValue === undefined
				&& !changes.items.currentValue?.length,
		);
	}

	ngOnDestroy() {
		this.removeClick();
		this.destroy$.next();
		this.destroy$.complete();
	}
}
